import _ from 'lodash'
import PropTypes from 'prop-types'
import { useFormContext, useController } from 'react-hook-form'

import { useFormField } from '@/shared/ui/Form/FormGroup'
import getInputClasses from '@/shared/ui/Form/lib/input-classes'
import getRules from '@/shared/ui/Form/lib/rules'

const FormControl = ({
  name,
  control,
  size,
  solid,
  white,
  flush,
  shadow,
  className,
  defaultValue = '',
  required,
  min,
  max,
  minLength,
  maxLength,
  pattern,
  validate,
  onChange,
  beforeChange,
  ...rest
}) => {
  const {
    field: { ref, value, onChange: onChangeField, ...inputProps },
    fieldState: { invalid },
    formState: { errors }
  } = useController({
    name,
    control,
    rules: getRules({
      required,
      min,
      max,
      minLength,
      maxLength,
      pattern,
      validate
    }),
    defaultValue
  })

  const field = useFormField()
  const inheritsField = !!field && (field.name == null || field.name === name)
  const isInvalid = !!errors[name] || invalid
  const classes = getInputClasses(
    { size, solid, white, flush, shadow, className },
    isInvalid
  )

  const handleChange = e => {
    let event = e
    if (beforeChange && _.isFunction(beforeChange)) event = beforeChange(event)
    onChangeField(event)
    onChange && onChange(event)
  }

  return (
    <input
      ref={ref}
      className={classes}
      value={value ?? ''}
      onChange={handleChange}
      aria-invalid={isInvalid || undefined}
      // Identidade herdada do FormGroup quando o controle é aninhado como
      // children — o padrão dominante neste app. Prop explícita sempre vence
      // (vem depois, no spread de `rest`).
      //
      // O `FormGroup` normalmente não declara `name` — ele fica no controle
      // aninhado. Por isso a herança vale quando o grupo não nomeia campo
      // algum, e é restrita ao campo certo quando nomeia.
      id={inheritsField ? field.controlId : undefined}
      aria-labelledby={inheritsField && !rest['aria-label'] ? field.labelId : undefined}
      aria-describedby={inheritsField ? field.describedBy : undefined}
      {...inputProps}
      {...rest}
      onWheel={e => (rest.type == 'number' ? e.currentTarget.blur() : null)}
    />
  )
}

const FormControlContainer = ({ name, ...rest }) => {
  const { control } = useFormContext()
  return <FormControl name={name} control={control} {...rest} />
}

FormControlContainer.propTypes = {
  name: PropTypes.string.isRequired
}

export { FormControlContainer as FormControl }
export default FormControlContainer
